import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Calendar, Search, Eye, Trash2, Download, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ConclaveRegistrations = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/conclave-registrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistrations(response.data.registrations || []);
    } catch (error) {
      toast.error('Failed to fetch registrations');
    } finally {
      setLoading(false);
    }
  };

  const deleteRegistration = async (id) => {
    if (!window.confirm('Are you sure you want to delete this registration?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/conclave-registrations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Registration deleted successfully');
      fetchRegistrations();
    } catch (error) {
      toast.error('Failed to delete registration');
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'City', 'State', 'Occupation', 'Organization', 'Registration Date'];
    const csvData = registrations.map(r => [
      r.fullName,
      r.email,
      r.phone,
      r.city,
      r.state,
      r.occupation,
      r.organization || '-',
      new Date(r.registrationDate).toLocaleDateString()
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'conclave_registrations.csv';
    a.click();
  };

  const filteredRegistrations = registrations.filter(reg =>
    reg.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.phone?.includes(searchTerm) ||
    reg.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif font-bold text-3xl text-primary mb-2">Conclave Registrations</h1>
          <p className="text-muted-foreground">Khatri Conclave 2026 registrations</p>
        </div>
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-muted-foreground" />
            <span className="text-lg font-semibold">{registrations.length} Registrations</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <Card className="p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, phone, or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Registrations Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Contact</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Location</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Occupation</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Registered</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    Loading registrations...
                  </td>
                </tr>
              ) : filteredRegistrations.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    No registrations found
                  </td>
                </tr>
              ) : (
                filteredRegistrations.map((reg) => (
                  <tr key={reg.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-foreground">{reg.fullName}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm">{reg.email}</p>
                      <p className="text-sm text-muted-foreground">{reg.phone}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm">{reg.city}, {reg.state}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm">{reg.occupation}</p>
                      <p className="text-sm text-muted-foreground">{reg.organization || '-'}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(reg.registrationDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedRegistration(reg)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteRegistration(reg.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail Modal */}
      {selectedRegistration && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedRegistration(null)}>
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="font-serif font-bold text-2xl text-primary">{selectedRegistration.fullName}</h2>
                  <p className="text-muted-foreground">Conclave Registration</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedRegistration.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedRegistration.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">City</p>
                  <p className="font-medium">{selectedRegistration.city}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">State</p>
                  <p className="font-medium">{selectedRegistration.state}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Occupation</p>
                  <p className="font-medium">{selectedRegistration.occupation}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Organization</p>
                  <p className="font-medium">{selectedRegistration.organization || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Designation</p>
                  <p className="font-medium">{selectedRegistration.designation || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Accompanying Persons</p>
                  <p className="font-medium">{selectedRegistration.accompanyingPersons}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Areas of Interest</p>
                  <p className="font-medium">{selectedRegistration.areasOfInterest?.join(', ') || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Attendance Preference</p>
                  <p className="font-medium">{selectedRegistration.attendancePreference?.join(', ') || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Special Requirements</p>
                  <p className="font-medium">{selectedRegistration.specialRequirements || '-'}</p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setSelectedRegistration(null)}>
                  Close
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ConclaveRegistrations;
